import jsPDF from "jspdf";
import pumpImage from "../../../assets/AquaThermPro-1.png";
import multiplePumpsImage from "../../../assets/pump-double-image.png";
import aquaThermImage from "../../../assets/AquaTherm-1.png";
import aquaThermProImage from "../../../assets/AquaThermPro-1.png";
import { PumpModel, PumpType } from "../../../constants/pumpData";
import { indexToMonth } from "../../../constants/months";

interface FirstHeating {
  minMonth: number;
  maxMonth: number;
  min: number;
  max: number;
}

interface Recommendation {
  type: string;
  totalPrice?: number;
  firstHeating?: FirstHeating;
  highestSpeedRate?: number;
  annualCOP?: number;
  pumpModel?: PumpModel;
}

const getPumpImage = (pumpModel?: PumpModel): string => {
  if (!pumpModel) return pumpImage;

  switch (pumpModel.type) {
    case PumpType.Aquatherm:
      return aquaThermImage;
    case PumpType.AquathermPro:
      return aquaThermProImage;
    default:
      return pumpImage;
  }
};

const addText = (
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  bold = false,
  size?: number
) => {
  if (bold) pdf.setFont("NotoSans-Bold", "normal");
  if (size) pdf.setFontSize(size);

  pdf.text(text, x, y);
  if (bold) pdf.setFont("NotoSans-Regular", "normal");
};

const renderLine = (pdf: jsPDF, x: number, y: number, length: number = 80) => {
  pdf.setDrawColor(238, 238, 238);
  pdf.line(x, y, x + length, y);
};

const renderKeyValue = (
  pdf: jsPDF,
  label: string,
  value: string | number,
  x: number,
  y: number
) => {
  pdf.text(label, x, y);
  addText(pdf, String(value), x + 55, y, true);
};

const renderRecommendationDetails = (
  pdf: jsPDF,
  recommendation: Recommendation,
  startX: number,
  startY: number,
  isMultiplePumps: boolean,
  airTemperatureLabel: string = "27°C"
) => {
  pdf.setFontSize(12);
  addText(pdf, "Recommended Model", startX, startY + 10);
  addText(pdf, `(KW @ Air ${airTemperatureLabel})`, startX, startY + 15);

  addText(
    pdf,
    recommendation.pumpModel?.model || "Model N/A",
    startX,
    startY + 20,
    true
  );

  if (isMultiplePumps)
    addText(pdf, "Require Approx: 2 units", startX, startY + 24, true, 8);
  if (isMultiplePumps)
    addText(
      pdf,
      "Information below is for single unit:",
      startX,
      startY + 47,
      false,
      6
    );

  renderLine(pdf, startX, startY + 48);

  pdf.setFontSize(8);
  renderKeyValue(
    pdf,
    "Electricity cost per year (estimated):",
    `$${recommendation.totalPrice?.toFixed(2) || "N/A"}`,
    startX,
    startY + 55
  );

  const { min, max, minMonth, maxMonth } = recommendation.firstHeating || {};
  renderKeyValue(
    pdf,
    "First heating time (hours):",
    `Min Month (${indexToMonth(minMonth || 0)}) ${min?.toFixed(2) || "N/A"}`,
    startX,
    startY + 60
  );
  renderKeyValue(
    pdf,
    "(Running 24h/day):",
    `Max Month (${indexToMonth(maxMonth || 0)}) ${max?.toFixed(2) || "N/A"}`,
    startX,
    startY + 65
  );

  renderAdditionalSpecs(pdf, recommendation, startX, startY + 70);
};

const renderAdditionalSpecs = (
  pdf: jsPDF,
  recommendation: Recommendation,
  startX: number,
  startY: number
) => {
  const { pumpModel } = recommendation;
  renderKeyValue(
    pdf,
    "Highest speed rate:",
    `${recommendation.highestSpeedRate?.toFixed(2) || "N/A"}%`,
    startX,
    startY
  );
  renderKeyValue(
    pdf,
    "Annual COP:",
    recommendation.annualCOP?.toFixed(2) || "N/A",
    startX,
    startY + 5
  );
  renderKeyValue(
    pdf,
    "Wifi support:",
    pumpModel?.wifiSupport ? "Yes" : "No",
    startX,
    startY + 10
  );
  renderKeyValue(
    pdf,
    "Sound Level 1m dB(A):",
    pumpModel?.soundLevel || "N/A",
    startX,
    startY + 15
  );
  renderKeyValue(
    pdf,
    "Dimensions:",
    `${pumpModel?.length || "N/A"} x ${pumpModel?.height || "N/A"} x ${
      pumpModel?.width || "N/A"
    } mm`,
    startX,
    startY + 20
  );
  renderKeyValue(
    pdf,
    "Net weight:",
    `${pumpModel?.netWeigth || "N/A"} kg`,
    startX,
    startY + 25
  );
  renderKeyValue(
    pdf,
    "Power Supply:",
    pumpModel?.powerSuply || "N/A",
    startX,
    startY + 30
  );
};

const renderRecommendation = (
  pdf: jsPDF,
  recommendation: Recommendation,
  startX: number,
  startY: number,
  label: string,
  multiplePumps: boolean,
  airTemperatureLabel: string = "27°C"
) => {
  pdf
    .setDrawColor(255, 215, 0)
    .setLineWidth(0.2)
    .rect(startX - 5, startY - 5, 95, 110);
  addText(pdf, label, startX, startY + 5, true);

  const imageToUse = multiplePumps
    ? multiplePumpsImage
    : getPumpImage(recommendation.pumpModel);

  pdf.addImage(imageToUse, "PNG", startX + 42, startY + 10, 40, 30);
  renderRecommendationDetails(
    pdf,
    recommendation,
    startX,
    startY,
    multiplePumps,
    airTemperatureLabel
  );
};

export const createRecommendationImages = ({
  pdf,
  recommendation1,
  recommendation2,
  isMultiplePumps,
  airTemperatureLabel = "27°C",
}: {
  pdf: jsPDF;
  recommendation1?: Recommendation;
  recommendation2?: Recommendation;
  isMultiplePumps: boolean;
  airTemperatureLabel?: string;
}) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const boxWidth = 90;
  const gap = 5;
  const startY = 100;
  const singleRecommendation = recommendation1 || recommendation2;

  if (isMultiplePumps) {
    renderRecommendation(
      pdf,
      singleRecommendation || { type: "Default" },
      (pageWidth - boxWidth) / 2,
      startY,
      "Best Option",
      true,
      airTemperatureLabel
    );
  } else if (recommendation1 && recommendation2) {
    const totalWidth = 2 * boxWidth + gap;
    const startX1 = (pageWidth - totalWidth) / 2;
    const startX2 = startX1 + boxWidth + gap;

    renderRecommendation(
      pdf,
      recommendation1,
      startX1,
      startY,
      "Option 1",
      false,
      airTemperatureLabel
    );
    renderRecommendation(
      pdf,
      recommendation2,
      startX2 + 5,
      startY,
      "Option 2",
      false,
      airTemperatureLabel
    );
  } else if (singleRecommendation) {
    renderRecommendation(
      pdf,
      singleRecommendation,
      (pageWidth - boxWidth) / 2,
      startY + 5,
      "Option 1",
      false
    );
  }

  return pdf;
};
